import { scopeThreadRef } from "@cmmd-forge/client-runtime";
import { Schema } from "effect";
import {
  DEFAULT_PROVIDER_INTERACTION_MODE,
  DEFAULT_RUNTIME_MODE,
  type CmmdAnchorKind,
  type CommandId,
  type ModelSelection,
  OrchestrationDispatchCommandError,
  type ScopedProjectRef,
  type ScopedThreadRef,
  type ThreadId,
} from "@cmmd-forge/contracts";

const PROJECT_LEAD_THREAD_TITLE = "Project Lead";
export const PROJECT_LEAD_THREAD_GOAL = "forge:project-lead-task-creation";

interface ProjectLeadThreadCandidate {
  readonly id: ThreadId;
  readonly title: string;
  readonly anchorKind: CmmdAnchorKind;
  readonly anchorCmmdId: string | null;
  readonly goal?: string | null;
  readonly archivedAt?: string | null;
}

interface ProjectLeadThreadCreateCommand {
  readonly type: "thread.create";
  readonly commandId: CommandId;
  readonly threadId: ThreadId;
  readonly projectId: ScopedProjectRef["projectId"];
  readonly title: string;
  readonly modelSelection: ModelSelection;
  readonly runtimeMode: typeof DEFAULT_RUNTIME_MODE;
  readonly interactionMode: typeof DEFAULT_PROVIDER_INTERACTION_MODE;
  readonly branch: null;
  readonly worktreePath: null;
  readonly anchorKind: "project";
  readonly anchorCmmdId: string;
  readonly goal: string;
  readonly createdAt: string;
}

export interface EnsureProjectLeadTaskCreationThreadInput {
  readonly projectRef: ScopedProjectRef;
  readonly cmmdProjectId: string;
  readonly taskDraftId: string;
  readonly contextPrompt: string;
  /** Recreate the exact durable conversation referenced by an existing CMMD draft. */
  readonly requiredThreadId?: ThreadId;
}

export interface EnsureProjectLeadTaskCreationThreadResult {
  readonly created: boolean;
  readonly threadRef: ScopedThreadRef;
}

export interface ProjectLeadTaskCreationThreadDependencies {
  readonly findThreads: (projectRef: ScopedProjectRef) => readonly ProjectLeadThreadCandidate[];
  readonly createThreadId: () => ThreadId;
  readonly createCommandId: () => CommandId;
  readonly dispatchCreate: (command: ProjectLeadThreadCreateCommand) => Promise<void>;
  readonly resolveModelSelection: (projectRef: ScopedProjectRef) => Promise<ModelSelection>;
  readonly seedComposer: (
    threadRef: ScopedThreadRef,
    prompt: string,
    replaceExistingPrompt?: string,
  ) => void;
  readonly now?: () => string;
}

interface ProjectLeadBootstrap {
  readonly promise: Promise<EnsureProjectLeadTaskCreationThreadResult>;
  context: string;
  confirmedByStore: boolean;
  resolved: boolean;
}

const bootstrapsByProject = new Map<string, ProjectLeadBootstrap>();

function projectLeadBootstrapKey(input: EnsureProjectLeadTaskCreationThreadInput): string {
  return `${input.projectRef.environmentId}:${input.projectRef.projectId}:${input.cmmdProjectId}:${input.requiredThreadId ?? "canonical"}`;
}

function findCanonicalProjectLeadThread(
  input: EnsureProjectLeadTaskCreationThreadInput,
  dependencies: ProjectLeadTaskCreationThreadDependencies,
): ProjectLeadThreadCandidate | null {
  return (
    dependencies
      .findThreads(input.projectRef)
      .find(
        (thread) =>
          (input.requiredThreadId === undefined || thread.id === input.requiredThreadId) &&
          thread.archivedAt == null &&
          thread.goal === PROJECT_LEAD_THREAD_GOAL &&
          thread.anchorKind === "project" &&
          thread.anchorCmmdId === input.cmmdProjectId,
      ) ?? null
  );
}

const pendingCreateCommandsByProject = new Map<string, ProjectLeadThreadCreateCommand>();
const stagedComposerContextByProject = new Map<string, string>();
const stagedContextStorageKey = (key: string) => `forge:project-lead-composer-context:${key}`;

function readStagedContext(key: string): string | undefined {
  const memory = stagedComposerContextByProject.get(key);
  if (memory !== undefined) return memory;
  try {
    return globalThis.localStorage?.getItem(stagedContextStorageKey(key)) ?? undefined;
  } catch {
    return undefined;
  }
}

function writeStagedContext(key: string, context: string): void {
  stagedComposerContextByProject.set(key, context);
  try {
    globalThis.localStorage?.setItem(stagedContextStorageKey(key), context);
  } catch {
    // Exact replacement remains safe in memory when storage is unavailable.
  }
}

export class ProjectLeadThreadCreateRejectedError extends Error {
  override readonly name = "ProjectLeadThreadCreateRejectedError";
}

function isDefinitiveDispatchRejection(error: unknown): boolean {
  return (
    Schema.is(OrchestrationDispatchCommandError)(error) ||
    error instanceof ProjectLeadThreadCreateRejectedError
  );
}

function buildProjectLeadComposerContext(input: EnsureProjectLeadTaskCreationThreadInput): string {
  return [
    "You are the Project Lead assisting with a Forge task-tree draft.",
    `CMMD project: ${input.cmmdProjectId}`,
    `Task draft: ${input.taskDraftId}`,
    "Use the project and task-draft context available to this conversation. Do not create tasks until the user confirms the reviewed tree.",
    "",
    input.contextPrompt.trim(),
  ].join("\n");
}

function stageContext(
  input: EnsureProjectLeadTaskCreationThreadInput,
  dependencies: ProjectLeadTaskCreationThreadDependencies,
  threadRef: ScopedThreadRef,
  replaceExistingPrompt?: string,
): void {
  const key = projectLeadBootstrapKey(input);
  const previousGeneratedContext = readStagedContext(key);
  const nextContext = buildProjectLeadComposerContext(input);
  dependencies.seedComposer(
    threadRef,
    nextContext,
    replaceExistingPrompt ?? previousGeneratedContext,
  );
  writeStagedContext(key, nextContext);
}

async function createProjectLeadThread(
  input: EnsureProjectLeadTaskCreationThreadInput,
  dependencies: ProjectLeadTaskCreationThreadDependencies,
): Promise<EnsureProjectLeadTaskCreationThreadResult> {
  const existing = findCanonicalProjectLeadThread(input, dependencies);
  if (existing) {
    const threadRef = scopeThreadRef(input.projectRef.environmentId, existing.id);
    stageContext(input, dependencies, threadRef);
    return { created: false, threadRef };
  }

  const key = projectLeadBootstrapKey(input);
  let command = pendingCreateCommandsByProject.get(key);
  if (command === undefined) {
    const modelSelection = await dependencies.resolveModelSelection(input.projectRef);
    const inserted = findCanonicalProjectLeadThread(input, dependencies);
    if (inserted) {
      const threadRef = scopeThreadRef(input.projectRef.environmentId, inserted.id);
      stageContext(input, dependencies, threadRef);
      return { created: false, threadRef };
    }
    command = {
      type: "thread.create",
      commandId: dependencies.createCommandId(),
      threadId: input.requiredThreadId ?? dependencies.createThreadId(),
      projectId: input.projectRef.projectId,
      title: PROJECT_LEAD_THREAD_TITLE,
      modelSelection,
      runtimeMode: DEFAULT_RUNTIME_MODE,
      interactionMode: DEFAULT_PROVIDER_INTERACTION_MODE,
      branch: null,
      worktreePath: null,
      anchorKind: "project",
      anchorCmmdId: input.cmmdProjectId,
      goal: PROJECT_LEAD_THREAD_GOAL,
      createdAt: dependencies.now?.() ?? new Date().toISOString(),
    };
    pendingCreateCommandsByProject.set(key, command);
  }
  const threadRef = scopeThreadRef(input.projectRef.environmentId, command.threadId);
  try {
    await dependencies.dispatchCreate(command);
  } catch (error: unknown) {
    if (isDefinitiveDispatchRejection(error)) pendingCreateCommandsByProject.delete(key);
    throw error;
  }
  pendingCreateCommandsByProject.delete(key);
  stageContext(input, dependencies, threadRef);
  return { created: true, threadRef };
}

export function ensureProjectLeadTaskCreationThread(
  input: EnsureProjectLeadTaskCreationThreadInput,
  dependencies: ProjectLeadTaskCreationThreadDependencies,
): Promise<EnsureProjectLeadTaskCreationThreadResult> {
  const key = projectLeadBootstrapKey(input);
  const canonicalThread = findCanonicalProjectLeadThread(input, dependencies);
  if (canonicalThread) {
    const threadRef = scopeThreadRef(input.projectRef.environmentId, canonicalThread.id);
    stageContext(input, dependencies, threadRef, bootstrapsByProject.get(key)?.context);
    const result = { created: false, threadRef };
    bootstrapsByProject.set(key, {
      promise: Promise.resolve(result),
      context: buildProjectLeadComposerContext(input),
      confirmedByStore: true,
      resolved: true,
    });
    return Promise.resolve(result);
  }

  let existingBootstrap = bootstrapsByProject.get(key);
  if (existingBootstrap?.confirmedByStore === true) {
    bootstrapsByProject.delete(key);
    existingBootstrap = undefined;
  }
  if (existingBootstrap) {
    const nextContext = buildProjectLeadComposerContext(input);
    if (existingBootstrap.context === nextContext) {
      return existingBootstrap.promise;
    }
    existingBootstrap.context = nextContext;
    return existingBootstrap.promise.then((result) => {
      dependencies.seedComposer(result.threadRef, nextContext);
      return result;
    });
  }

  const bootstrap = createProjectLeadThread(input, dependencies).catch((error: unknown) => {
    bootstrapsByProject.delete(key);
    throw error;
  });
  bootstrapsByProject.set(key, {
    promise: bootstrap,
    context: buildProjectLeadComposerContext(input),
    confirmedByStore: false,
    resolved: false,
  });
  void bootstrap.then(
    () => {
      const current = bootstrapsByProject.get(key);
      if (current?.promise === bootstrap) current.resolved = true;
    },
    () => undefined,
  );
  return bootstrap;
}

export function clearProjectLeadTaskCreationThreadBootstrapForTests(): void {
  bootstrapsByProject.clear();
  pendingCreateCommandsByProject.clear();
  stagedComposerContextByProject.clear();
}
